import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ApprovedUser } from '@/schemas/admin.types';
import { formatDate } from '@/components/ui/styles/adminHelpers';
import UserAvatar from '../ui/UserAvatar';
import RoleBadge from '../ui/RoleBadge';
import ActionButtons from '../ui/ActionButtons';

interface ApprovedUsersTableProps {
  users: ApprovedUser[];
  onDeleteUser?: (user_id: string) => void;
  deleting?: string | null;
  userVideoCountMap?: Map<string, number>;
  videoSortOrder?: 'asc' | 'desc' | null;
  onToggleVideoSort?: (value: 'asc' | 'desc' | '') => void;
}

/**
 * ApprovedUsersTable - Table component for displaying approved users
 * 
 * Features a comprehensive list of approved users with search functionality,
 * role-based styling, and delete controls for non-admin users.
 * 
 * Features:
 * - Real-time search filtering
 * - User avatars with initials
 * - Role badges with color coding
 * - Admin protection (cannot delete)
 * - Loading states for delete operations
 * - Empty states for no users/no search results
 * - Responsive design
 * 
 * @param users - Array of approved users
 * @param onDeleteUser - Optional function to delete a user
 * @param deleting - ID of user currently being deleted (for loading state)
 */
const ApprovedUsersTable: React.FC<ApprovedUsersTableProps> = ({
  users,
  onDeleteUser,
  deleting = null,
  userVideoCountMap,
  videoSortOrder = null,
  onToggleVideoSort
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  /**
   * Memoized filtered users based on search term
   * Searches across name, email, and role fields
   */
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    
    const term = searchTerm.toLowerCase();
    return users.filter(user => 
      user.name.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      user.role.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  /**
   * Handles user deletion with confirmation and admin protection
   */
  const handleDeleteClick = (userId: string, userRole: string) => {
    if (userRole === 'admin') {
      globalThis.alert('Cannot delete admin users - they are protected from deletion');
      return;
    }
    
    if (globalThis.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      onDeleteUser?.(userId);
    }
  };

  // Empty state for no users
  if (users.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No approved users</h3>
        <p className="text-gray-500">Users will appear here once they are approved</p>
      </div>
    );
  }

  const handleSortClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSortOptionClick = (value: 'asc' | 'desc' | '') => {
    if (onToggleVideoSort) {
      onToggleVideoSort(value);
    }
    setIsDropdownOpen(false);
  };

  const renderSortIcon = () => {
    if (videoSortOrder === 'desc') {
      return (
        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
        </svg>
      );
    }
    if (videoSortOrder === 'asc') {
      return (
        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4 4m0 0l4-4m-4 4V8" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
      </svg>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* Search Bar and Sort Controls */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-white">
        {onToggleVideoSort && (
          <div className="relative flex-shrink-0" ref={dropdownRef}>
            <button
              onClick={handleSortClick}
              className="flex items-center justify-center p-2 text-gray-700 bg-white hover:bg-gray-50 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
              title="Sort by videos"
            >
              {renderSortIcon()}
            </button>
            
            {isDropdownOpen && (
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <div className="py-1">
                  <button
                    onClick={() => handleSortOptionClick('')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      videoSortOrder ? 'bg-white text-gray-700' : 'bg-gray-50 text-indigo-600 font-medium'
                    }`}
                  >
                    No Sort
                  </button>
                  <button
                    onClick={() => handleSortOptionClick('desc')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      videoSortOrder === 'desc' ? 'bg-gray-50 text-indigo-600 font-medium' : 'bg-white text-gray-700'
                    }`}
                  >
                    Most Videos First
                  </button>
                  <button
                    onClick={() => handleSortOptionClick('asc')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      videoSortOrder === 'asc' ? 'bg-gray-50 text-indigo-600 font-medium' : 'bg-white text-gray-700'
                    }`}
                  >
                    Least Videos First
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search users by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors"
            />
          </div>
          {searchTerm && (
            <p className="mt-1 text-xs text-gray-600">
              Showing {filteredUsers.length} of {users.length} results
            </p>
          )}
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Videos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Approved Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Approved By
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors duration-150">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <UserAvatar 
                      name={user.name} 
                      email={user.email} 
                      size="md" 
                      showGradient={true}
                      className="mr-3"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900 text-left">{user.name}</div>
                      <div className="text-sm text-gray-500 truncate max-w-48" title={user.email}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex justify-start">
                    <RoleBadge role={user.role} />
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm text-gray-900">
                    <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>{userVideoCountMap?.get(user.email) || 0}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(user.approved_at)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <UserAvatar
                      name={user.approved_by}
                      email={user.approved_by || 'System'}
                      size="sm"
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-900">{user.approved_by || 'System'}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex justify-start">
                    <ActionButtons 
                      showDelete={true}
                      onDelete={() => handleDeleteClick(user.id, user.role)}
                      isDeleting={deleting === user.id}
                      isAdmin={user.role === 'admin'}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* No search results */}
      {filteredUsers.length === 0 && searchTerm && (
        <div className="text-center py-8 border-t border-gray-200">
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <p className="text-gray-500 text-sm">No users found matching "{searchTerm}"</p>
          <button
            onClick={() => setSearchTerm('')}
            className="mt-2 text-indigo-600 hover:text-indigo-500 text-sm font-medium"
          >
            Clear search
          </button>
        </div>
      )}
    </div>
  );
};

export default ApprovedUsersTable;
